import React, { useState, useEffect } from 'react';
import { useSpeech } from '../hooks/useSpeech';
import './MultipleChoice.css';

export const MultipleChoice = ({
  card,
  direction,
  allCards,
  onAnswer,
  onNext,
  counter,
  score,
  autoAdvance = true
}) => {
  const [options, setOptions] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [answered, setAnswered] = useState(false);
  const { isSupported, isSpeaking, speak } = useSpeech();

  // Generate multiple choice options
  useEffect(() => {
    if (!card || !allCards || allCards.length < 4) {
      setOptions([]);
      return;
    }

    // Get the correct answer
    const correctAnswer = direction === 'ar' ? card.en : card.ar;

    // Get wrong answers from other cards
    const wrongCards = allCards.filter(c => c !== card);
    const shuffled = [...wrongCards].sort(() => Math.random() - 0.5);
    const wrongAnswers = shuffled
      .slice(0, 3)
      .map(c => direction === 'ar' ? c.en : c.ar);

    // Combine and shuffle all options
    const allOptions = [correctAnswer, ...wrongAnswers];
    const shuffledOptions = allOptions.sort(() => Math.random() - 0.5);

    setOptions(shuffledOptions);
    setSelectedAnswer(null);
    setIsCorrect(null);
    setAnswered(false);
  }, [card, allCards, direction]);

  const handleAnswerClick = (option) => {
    if (selectedAnswer !== null) return; // Already answered

    const correctAnswer = direction === 'ar' ? card.en : card.ar;
    const correct = option === correctAnswer;

    setSelectedAnswer(option);
    setIsCorrect(correct);
    setAnswered(true);

    // Notify parent
    onAnswer(correct);

    // Auto-advance if enabled
    if (autoAdvance && onNext) {
      setTimeout(() => {
        onNext();
      }, 1500); // 1.5 second delay to see feedback
    }
  };

  const handleNext = () => {
    if (onNext) {
      onNext();
    }
  };

  const getButtonClass = (option) => {
    if (selectedAnswer === null) return 'mc-option';

    const correctAnswer = direction === 'ar' ? card.en : card.ar;

    if (option === correctAnswer) {
      return 'mc-option mc-correct';
    }

    if (option === selectedAnswer && !isCorrect) {
      return 'mc-option mc-incorrect';
    }

    return 'mc-option mc-disabled';
  };

  if (!card || options.length === 0) {
    return (
      <div className="mc-card">
        <div className="mc-message">
          Not enough cards for quiz mode. Need at least 4 cards.
        </div>
      </div>
    );
  }

  const question = direction === 'ar' ? card.ar : card.en;

  return (
    <div className="mc-card">
      <div className="mc-header">
        <div className="mc-counter">{counter}</div>
        <div className="mc-score">Score: {score.correct}/{score.total}</div>
      </div>

      <div className="mc-question-section">
        <div className="mc-question-header">
          <div className="mc-question-label">
            {direction === 'ar' ? 'What does this mean?' : 'ما هي الترجمة؟'}
          </div>
          {card.dialect && <div className="dialect-badge-small">Dialect</div>}
        </div>
        <div className="mc-question" dir={direction === 'ar' ? 'rtl' : 'ltr'}>
          {question}
        </div>
        {isSupported && (
          <button
            className={`speak-btn ${isSpeaking ? 'speaking' : ''}`}
            onClick={() => speak(question, direction === 'ar' ? 'ar-SA' : 'en-US')}
            title="Listen to pronunciation"
          >
            🔊
          </button>
        )}
      </div>

      <div className="mc-options">
        {options.map((option, index) => (
          <button
            key={index}
            className={getButtonClass(option)}
            onClick={() => handleAnswerClick(option)}
            disabled={selectedAnswer !== null}
          >
            <span className="mc-option-number">{index + 1}</span>
            <span className="mc-option-text" dir={direction === 'ar' ? 'ltr' : 'rtl'}>
              {option}
            </span>
          </button>
        ))}
      </div>

      {selectedAnswer !== null && (
        <>
          <div className={`mc-feedback ${isCorrect ? 'mc-feedback-correct' : 'mc-feedback-incorrect'}`}>
            {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
          </div>
          {!autoAdvance ? (
            <button className="mc-next-btn" onClick={handleNext}>
              Next →
            </button>
          ) : (
            <div className="mc-hint">Moving to next question...</div>
          )}
        </>
      )}

      {selectedAnswer === null && (
        <div className="mc-hint">
          Use number keys (1-4) to select an answer
        </div>
      )}
    </div>
  );
};
